import React, { useEffect, useState } from 'react';
import { Form, Input, FormInstance } from 'antd';
import * as S from './index.styled';
import axiosRequest from '@/api';
import { ResData } from '@/@types';
import { CheckCircleFilled, LoadingOutlined } from '@ant-design/icons';

interface InformationInputNewUserProps {
  form: FormInstance;
  stepData: (data: Record<string, any>) => void;
  existingData: Record<string, any>;
  isEmailVerified: boolean;
  setIsEmailVerified: (verified: boolean) => void;
}

const checkEmailDuplicate = async (email: string): Promise<boolean> => {
  try {
    const response = await axiosRequest.requestAxios<ResData<{ email_exists: boolean }>>(
      'get',
      `/check_email?email=${email}`,
    );
    return response.data.email_exists;
  } catch (error) {
    console.error('Error checking email duplication:', error);
    throw new Error('Failed to check email duplication');
  }
};

const InformationInputNewUser: React.FC<InformationInputNewUserProps> = ({ form, stepData, existingData, isEmailVerified, setIsEmailVerified }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEmailValid, setIsEmailValid] = useState<boolean>(false);

  useEffect(() => {
    form.setFieldsValue(existingData);
  }, [existingData, form]);

  const handleCheckEmail = async () => {
    try {
      const email = form.getFieldValue('email');
      const emailError = form.getFieldError('email');
      if (emailError.length > 0) return;

      setIsLoading(true);
      const emailExists = await checkEmailDuplicate(email);
      if (emailExists) {
        form.setFields([
          {
            name: 'email',
            errors: ['이미 가입된 이메일입니다.'],
          },
        ]);
        setIsEmailVerified(false);
      } else {
        form.setFields([
          {
            name: 'email',
            errors: [],
          },
        ]);
        setIsEmailVerified(true);
      }
    } catch (error) {
      form.setFields([
        {
          name: 'email',
          errors: ['이메일 중복 체크에 실패했습니다.'],
        },
      ]);
      setIsEmailVerified(false);
    } finally {
      setTimeout(() => setIsLoading(false), 1000); // 1초 후 로딩 상태 해제
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setIsEmailVerified(false);
    setIsEmailValid(validateEmail(email));
    form.setFieldsValue({ email });
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumbers = (_: any, value: any) => {
    const phone1 = form.getFieldValue('phone1');
    const phone2 = form.getFieldValue('phone2');
    const phone3 = form.getFieldValue('phone3');

    if (!phone1 || !phone2 || !phone3) {
      return Promise.reject(new Error('전화번호를 모두 입력해주세요.'));
    }
    return Promise.resolve();
  };

  return (
    <S.Container>
      <S.FormTitle>사용자 정보 입력</S.FormTitle>
      <S.FormExplanation>최초 가입 정보가 회사 담당자 정보로 저장됩니다.</S.FormExplanation>
      <Form form={form} layout="vertical" onFinish={stepData} initialValues={existingData}>
        <S.StyledTable>
          <tbody>
            <tr>
              <td>
                이메일<S.StyledStarSign>*</S.StyledStarSign>
              </td>
              <td>
                <S.FlexContainer>
                  <S.StyledFormItem
                    name="email"
                    rules={[
                      { required: true, message: '중복확인을 완료해야 회원가입버튼이 활성화됩니다.' },
                      {
                        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'wepick@wepick.com 형식으로 입력해주세요.',
                      },
                    ]}
                  >
                    <S.StyledInput onChange={handleEmailChange} />
                  </S.StyledFormItem>
                  <S.DuplicateCheckButton onClick={handleCheckEmail} disabled={!isEmailValid}>
                    <div>
                      {isLoading ? (
                        <LoadingOutlined style={{ color: 'white' }} />
                      ) : isEmailVerified ? (
                        <CheckCircleFilled style={{ color: 'white' }} />
                      ) : (
                        '중복확인'
                      )}
                    </div>
                  </S.DuplicateCheckButton>
                </S.FlexContainer>
              </td>
            </tr>
            <tr>
              <td>
                비밀번호<S.StyledStarSign>*</S.StyledStarSign>
              </td>
              <td>
                <S.StyledFormItem
                  name="password"
                  rules={[
                    { required: true, message: '비밀번호를 입력해주세요.' },
                    { min: 8, message: '비밀번호는 최소 8자리여야 합니다.' },
                    {
                      pattern: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                      message: '비밀번호는 숫자와 특수문자를 포함해야 합니다.',
                    },
                  ]}
                >
                  <S.StyledInput type="password" />
                </S.StyledFormItem>
              </td>
            </tr>
            <tr>
              <td>
                이름<S.StyledStarSign>*</S.StyledStarSign>
              </td>
              <td>
                <S.StyledFormItem name="name" rules={[{ required: true, message: '이름을 입력해주세요.' }]}>
                  <S.StyledInput />
                </S.StyledFormItem>
              </td>
            </tr>
            <tr>
              <td>
                부서<S.StyledStarSign>*</S.StyledStarSign>
              </td>
              <td>
                <S.StyledFormItem name="department" rules={[{ required: true, message: '부서를 입력해주세요.' }]}>
                  <S.StyledInput />
                </S.StyledFormItem>
              </td>
            </tr>
            <tr>
              <td>
                휴대폰번호<S.StyledStarSign>*</S.StyledStarSign>
              </td>
              <td>
                <S.StyledFormItem name="phoneGroup" rules={[{ validator: validatePhoneNumbers }]}>
                  <S.StyledInputGroup>
                    <S.StyledFormItem name="phone1" noStyle>
                      <S.StyledPhoneInput maxLength={3} />
                    </S.StyledFormItem>
                    <span>-</span>
                    <S.StyledFormItem name="phone2" noStyle>
                      <S.StyledPhoneInput maxLength={4} />
                    </S.StyledFormItem>
                    <span>-</span>
                    <S.StyledFormItem name="phone3" noStyle>
                      <S.StyledPhoneInput maxLength={4} />
                    </S.StyledFormItem>
                  </S.StyledInputGroup>
                </S.StyledFormItem>
              </td>
            </tr>
          </tbody>
        </S.StyledTable>
      </Form>
    </S.Container>
  );
};

export default InformationInputNewUser;
